import { apiErrorMessage, handleLinkedStockDependentsError } from "components/ManageProduct/LinkedStockDependentsModal";
import styled from "@emotion/styled";
import { PlusOutlined, ReloadOutlined } from "@ant-design/icons";
import {
  Button,
  Form,
  Input,
  InputNumber,
  Modal,
  Segmented,
  Skeleton,
  Table,
  Tag,
  Typography,
  message,
} from "antd";
import { FC, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import { useAppSelector } from "store";
import { useGetInventoryByFactoryId } from "hooks/useProduct";
import {
  useAdjustGeneralStock,
  useDeleteGeneralStock,
  useGeneralStockTransactions,
} from "hooks/useGeneralStock";
import { numberFormatInt } from "utils/nuberFormat";
import { createInputNumberGuards } from "utils";
import { inventoryGeneralToFormStock } from "services/interface/generalStock.interface";
import type { ProductInventoryItem } from "services/interface";
import MasterStockTable, {
  type MasterStockSortDir,
  type MasterStockSortField,
} from "components/ManageProduct/MasterStockTable";
import GeneralStockFormModal from "components/ManageProduct/GeneralStock/GeneralStockFormModal";
import GeneralStockSafeStockModal from "components/ManageProduct/GeneralStock/GeneralStockSafeStockModal";
import InventorySkuEditModal from "components/ManageProduct/InventorySkuEditModal";
import SearchProductForm from "components/ManageProduct/SearchProductForm";
import { MASTER_STOCK_LABEL } from "components/ManageProduct/Recipe/bundleLabels";
import {
  formatGeneralStockDisplayName,
  inventoryItemMatchesSearch,
  isInventoryGeneralItem,
  isInventorySkuItem,
} from "components/ManageProduct/inventoryHelpers";

/** state ที่จำไว้ตอนออกไปหน้าแก้ไขสินค้า เพื่อคืนค่าเมื่อกดกลับมา */
type SavedMasterStockState = {
  searchInput?: string;
  sortBy?: MasterStockSortField | null;
  sortDir?: MasterStockSortDir | null;
  scrollTop?: number;
};
const MASTER_STOCK_STATE_KEY = "warehouseMasterStockState";

const readSavedMasterStockState = (): SavedMasterStockState | null => {
  try {
    const raw = sessionStorage.getItem(MASTER_STOCK_STATE_KEY);
    return raw ? (JSON.parse(raw) as SavedMasterStockState) : null;
  } catch {
    return null;
  }
};

/** หา element ที่ scroll ได้จริง (parent ที่ overflow-y auto/scroll) ไม่งั้น fallback เป็น window */
const getScrollParent = (el: HTMLElement | null): HTMLElement | Window => {
  let node = el?.parentElement ?? null;
  while (node) {
    const style = window.getComputedStyle(node);
    if (
      (style.overflowY === "auto" || style.overflowY === "scroll") &&
      node.scrollHeight > node.clientHeight
    ) {
      return node;
    }
    node = node.parentElement;
  }
  return window;
};

const getScrollTop = (target: HTMLElement | Window): number =>
  target instanceof Window ? target.scrollY : target.scrollTop;

/** มาสเตอร์สต็อก — แยกออกจากหน้าจัดการสินค้า มาอยู่หน้า "คลังสินค้า" (self-contained) */
const MasterStockPanel: FC = () => {
  const navigate = useNavigate();
  /** อ่าน state ที่จำไว้ครั้งเดียว (ตอน mount) สำหรับคืนค่า scroll/ค้นหา/sort */
  const savedStateRef = useRef<SavedMasterStockState | null>(
    readSavedMasterStockState(),
  );
  const rootRef = useRef<HTMLDivElement>(null);
  const didRestoreScrollRef = useRef(false);
  const integerGuards = createInputNumberGuards({ mode: "integer" });
  const factoryId = useAppSelector(
    (state) => state.auth.profile?.user?.role?.ref_id ?? "",
  );

  const [searchInput, setSearchInput] = useState(
    savedStateRef.current?.searchInput ?? "",
  );

  const [generalStockCreateOpen, setGeneralStockCreateOpen] = useState(false);
  const [generalStockEdit, setGeneralStockEdit] =
    useState<ProductInventoryItem | null>(null);
  const [generalStockSafeTarget, setGeneralStockSafeTarget] =
    useState<ProductInventoryItem | null>(null);
  /** SKU ที่กำลังแก้ไขจากตาราง (เปิด modal กำหนดสินค้า–ราคา/คลัง inline) */
  const [skuEditTarget, setSkuEditTarget] = useState<{
    productId: string;
    skuId: string;
  } | null>(null);
  /** แบ่งหมวด: ทั้งหมด | มาสเตอร์(general + sku basic) | ชุดเซ็ต(sku advanced) — default = มาสเตอร์ */
  const [categoryFilter, setCategoryFilter] = useState<"all" | "master" | "set">(
    "master",
  );

  // เรียงตามสต็อก (ทำที่ backend) — คืนค่าจาก state ที่จำไว้ถ้ามี
  const [sortBy, setSortBy] = useState<MasterStockSortField | null>(
    savedStateRef.current?.sortBy ?? null,
  );
  const [sortDir, setSortDir] = useState<MasterStockSortDir | null>(
    savedStateRef.current?.sortDir ?? null,
  );
  const sortParam = useMemo(
    () => (sortBy && sortDir ? { sort_by: sortBy, sort_dir: sortDir } : undefined),
    [sortBy, sortDir],
  );

  const {
    data: inventoryData,
    isPending: inventoryPending,
    isFetching: inventoryFetching,
    refetch: refetchInventory,
  } = useGetInventoryByFactoryId(factoryId, !!factoryId, sortParam, true);

  const { mutate: deleteGeneralStockMutate, isPending: deletingGeneralStock } =
    useDeleteGeneralStock();
  const { mutateAsync: adjustGeneralStockMutate, isPending: adjustingGeneralStock } =
    useAdjustGeneralStock();

  const [adjustGeneralStockTarget, setAdjustGeneralStockTarget] =
    useState<ProductInventoryItem | null>(null);
  const [generalStockHistoryTarget, setGeneralStockHistoryTarget] =
    useState<ProductInventoryItem | null>(null);
  const [generalStockHistoryPage, setGeneralStockHistoryPage] = useState(1);
  const historyLimit = 10;
  const [adjustGeneralStockForm] = Form.useForm<{
    direction: "in" | "out";
    quantity: number;
    remark?: string;
  }>();

  const generalTxInput = useMemo(
    () => ({
      stock_id: generalStockHistoryTarget?.stock?.id ?? "",
      page: generalStockHistoryPage,
      limit: historyLimit,
    }),
    [generalStockHistoryTarget?.stock?.id, generalStockHistoryPage],
  );

  const { rows: generalTxRows, total: generalTxTotal, isFetching: generalTxLoading } =
    useGeneralStockTransactions(
      generalTxInput,
      !!generalStockHistoryTarget?.stock?.id,
    );

  const generalTxColumns = useMemo(
    () => [
      {
        title: "วันที่",
        dataIndex: "create_date",
        key: "create_date",
        width: 140,
        render: (d: string) => (d ? dayjs(d).format("DD/MM/YYYY HH:mm") : "-"),
      },
      {
        title: "ทิศทาง",
        dataIndex: "direction",
        key: "direction",
        width: 70,
        render: (d: string) =>
          d === "in" ? <Tag color="blue">เข้า</Tag> : <Tag color="volcano">ออก</Tag>,
      },
      {
        title: "จำนวน",
        dataIndex: "quantity",
        key: "quantity",
        width: 70,
        render: (q: number) => numberFormatInt(q ?? 0),
      },
      {
        title: "หมายเหตุ",
        dataIndex: "remark",
        key: "remark",
        ellipsis: true,
      },
    ],
    [],
  );

  const openAdjustGeneralStock = (item: ProductInventoryItem) => {
    setAdjustGeneralStockTarget(item);
    adjustGeneralStockForm.setFieldsValue({ direction: "in", quantity: 1, remark: "" });
  };

  const openGeneralStockHistory = (item: ProductInventoryItem) => {
    setGeneralStockHistoryTarget(item);
    setGeneralStockHistoryPage(1);
  };

  const submitAdjustGeneralStock = async () => {
    if (!adjustGeneralStockTarget?.stock?.id) return;
    try {
      const v = await adjustGeneralStockForm.validateFields();
      await adjustGeneralStockMutate({
        stock_id: adjustGeneralStockTarget.stock.id,
        direction: v.direction,
        quantity: v.quantity,
        remark: v.remark,
      });
      message.success(v.direction === "in" ? "เพิ่มสต็อกเรียบร้อย" : "ลดสต็อกเรียบร้อย");
      adjustGeneralStockForm.setFieldsValue({ direction: "in", quantity: 1, remark: "" });
      // บันทึกเสร็จ → ปิด modal ทันที
      setAdjustGeneralStockTarget(null);
    } catch (err: unknown) {
      const e = err as { errorFields?: unknown; message?: string };
      if (e?.errorFields) return;
      message.error(e?.message || "ไม่สามารถปรับสต็อกได้");
    }
  };

  const inventoryProducts = useMemo(
    () => inventoryData?.products ?? [],
    [inventoryData?.products],
  );
  const searchTrimmed = searchInput.trim().toLowerCase();

  const searchFiltered = useMemo(() => {
    if (!searchTrimmed) return inventoryProducts;
    return inventoryProducts.filter((p) =>
      inventoryItemMatchesSearch(p, searchTrimmed),
    );
  }, [inventoryProducts, searchTrimmed]);

  // รวมมาสเตอร์ + SKU(ที่เปิดสต็อก) ไว้ลิสต์เดียว เรียงตามลำดับจาก backend (รองรับ sort ฝั่ง backend)
  // SKU แสดงเฉพาะ is_stock=true ส่วน general เป็นสต็อกเสมอ
  const masterItems = useMemo(
    () =>
      searchFiltered
        .map((p) => {
          if (isInventoryGeneralItem(p)) return p;
          return {
            ...p,
            variants: (p.variants ?? [])
              .map((v) => ({
                ...v,
                items: (v.items ?? []).filter((it) => !!it.is_stock),
              }))
              .filter((v) => v.items.length > 0),
          };
        })
        .filter(
          (p) =>
            isInventoryGeneralItem(p) ||
            (p.variants ?? []).some((v) => v.items.length > 0),
        ),
    [searchFiltered],
  );

  // กรองตามหมวดที่เลือก (ทั้งหมด/มาสเตอร์/ชุดเซ็ต)
  const displayItems = useMemo(() => {
    if (categoryFilter === "all") return masterItems;
    if (categoryFilter === "master") {
      // มาสเตอร์ = general stock + SKU พื้นฐาน (stock_mode basic) รวมกัน
      return masterItems
        .map((p) => {
          if (isInventoryGeneralItem(p)) return p;
          return {
            ...p,
            variants: (p.variants ?? [])
              .map((v) => ({
                ...v,
                items: (v.items ?? []).filter(
                  (it) => (it.stock_mode ?? "basic") === "basic",
                ),
              }))
              .filter((v) => v.items.length > 0),
          };
        })
        .filter(
          (p) =>
            isInventoryGeneralItem(p) ||
            (p.variants ?? []).some((v) => v.items.length > 0),
        );
    }
    // ชุดเซ็ต = SKU ขั้นสูง (stock_mode advanced) เท่านั้น
    return masterItems
      .filter(isInventorySkuItem)
      .map((p) => ({
        ...p,
        variants: (p.variants ?? [])
          .map((v) => ({
            ...v,
            items: (v.items ?? []).filter(
              (it) => (it.stock_mode ?? "basic") === "advanced",
            ),
          }))
          .filter((v) => v.items.length > 0),
      }))
      .filter((p) => (p.variants ?? []).some((v) => v.items.length > 0));
  }, [masterItems, categoryFilter]);

  // กั้น isPending ด้วย !!factoryId — กรณี factoryId ยังไม่พร้อมทำให้ query disabled → isPending=true เสมอ
  const loadingView = (inventoryPending && !!factoryId) || (inventoryFetching && !inventoryData);

  /** ออกไปหน้าแก้ไขสินค้า — จำ scroll + ค้นหา + sort ไว้ก่อน เพื่อคืนค่าเมื่อกดกลับมา */
  const handleViewProduct = (productId: string) => {
    try {
      const target = getScrollParent(rootRef.current);
      const state: SavedMasterStockState = {
        searchInput,
        sortBy,
        sortDir,
        scrollTop: getScrollTop(target),
      };
      sessionStorage.setItem(MASTER_STOCK_STATE_KEY, JSON.stringify(state));
    } catch {
      /* sessionStorage ใช้ไม่ได้ก็ข้ามไป */
    }
    navigate(`/manage-product/edit/${productId}`);
  };

  /** คืนค่า scroll หลังข้อมูลโหลดเสร็จ (one-shot) แล้วล้าง state ที่จำไว้ */
  useEffect(() => {
    const saved = savedStateRef.current;
    if (!saved || didRestoreScrollRef.current) return;
    if (loadingView) return;
    didRestoreScrollRef.current = true;
    const y = saved.scrollTop ?? 0;
    const target = getScrollParent(rootRef.current);
    // รอให้ตาราง render เสร็จก่อนแล้วค่อยเลื่อน
    requestAnimationFrame(() => {
      if (target instanceof Window) target.scrollTo(0, y);
      else target.scrollTop = y;
    });
    try {
      sessionStorage.removeItem(MASTER_STOCK_STATE_KEY);
    } catch {
      /* ignore */
    }
    savedStateRef.current = null;
  }, [loadingView]);
  const hasSourceData =
    generalInventoryCount(inventoryProducts) > 0 ||
    skuInventoryCount(inventoryProducts) > 0;
  const hasVisible = displayItems.length > 0;

  return (
    <Wrap ref={rootRef}>
      <Toolbar>
        <CategorySlot>
          <Segmented
            value={categoryFilter}
            onChange={(v) => setCategoryFilter(v as "all" | "master" | "set")}
            options={[
              { value: "all", label: "ทั้งหมด" },
              { value: "master", label: "มาสเตอร์" },
              { value: "set", label: "ชุดเซ็ต" },
            ]}
          />
        </CategorySlot>
        <Actions>
          <Button
            icon={<ReloadOutlined />}
            loading={inventoryFetching}
            onClick={() => refetchInventory()}
          >
            รีโหลด
          </Button>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => setGeneralStockCreateOpen(true)}
          >
            เพิ่ม{MASTER_STOCK_LABEL}
          </Button>
        </Actions>
      </Toolbar>

      <SearchBar>
        <SearchProductForm
          searchInput={searchInput}
          setSearchInput={setSearchInput}
        />
      </SearchBar>

      <GeneralStockFormModal
        open={generalStockCreateOpen}
        onClose={() => setGeneralStockCreateOpen(false)}
      />
      <GeneralStockFormModal
        open={!!generalStockEdit}
        editing={generalStockEdit ? inventoryGeneralToFormStock(generalStockEdit) : null}
        onClose={() => setGeneralStockEdit(null)}
      />
      <GeneralStockSafeStockModal
        open={!!generalStockSafeTarget}
        item={generalStockSafeTarget}
        onClose={() => setGeneralStockSafeTarget(null)}
      />

      {/* ปรับสต็อก general */}
      <Modal
        title={
          adjustGeneralStockTarget ? (
            <span>
              ปรับสต็อก —{" "}
              <Typography.Text strong>
                {formatGeneralStockDisplayName(
                  adjustGeneralStockTarget.name,
                  adjustGeneralStockTarget.description,
                )}
              </Typography.Text>
            </span>
          ) : (
            "ปรับสต็อก"
          )
        }
        open={!!adjustGeneralStockTarget}
        maskClosable={!adjustingGeneralStock}
        onCancel={() => {
          if (adjustingGeneralStock) return;
          setAdjustGeneralStockTarget(null);
        }}
        onOk={submitAdjustGeneralStock}
        confirmLoading={adjustingGeneralStock}
        okText="บันทึก"
        cancelText="ปิด"
        destroyOnClose
        width={520}
      >
        <Form
          disabled={adjustingGeneralStock}
          form={adjustGeneralStockForm}
          layout="vertical"
          initialValues={{ direction: "in", quantity: 1 }}
        >
          <Form.Item label="คงเหลือปัจจุบัน">
            <Typography.Text strong>
              {numberFormatInt(adjustGeneralStockTarget?.stock?.stock_quantity ?? 0)} ชิ้น
            </Typography.Text>
          </Form.Item>
          <Form.Item label="การทำงาน" name="direction" rules={[{ required: true }]}>
            <Segmented
              block
              options={[
                { label: "เพิ่มเข้าคลัง", value: "in" },
                { label: "นำออกจากคลัง", value: "out" },
              ]}
            />
          </Form.Item>
          <Form.Item
            label="จำนวน"
            name="quantity"
            rules={[
              { required: true, message: "กรุณาระบุจำนวน" },
              {
                validator: (_, v) =>
                  v >= 1
                    ? Promise.resolve()
                    : Promise.reject(new Error("จำนวนต้องไม่น้อยกว่า 1")),
              },
            ]}
          >
            <InputNumber
              min={1}
              precision={0}
              step={1}
              style={{ width: "100%" }}
              {...integerGuards}
            />
          </Form.Item>
          <Form.Item label="หมายเหตุ (ถ้ามี)" name="remark">
            <Input.TextArea
              rows={2}
              placeholder="เช่น รับของจากซัพพลายเออร์ / ของเสียหาย / นับสต็อก"
            />
          </Form.Item>
        </Form>
      </Modal>

      {/* ประวัติเข้า-ออก general */}
      <Modal
        title={
          generalStockHistoryTarget ? (
            <span>
              ประวัติเข้า-ออก —{" "}
              <Typography.Text strong>
                {formatGeneralStockDisplayName(
                  generalStockHistoryTarget.name,
                  generalStockHistoryTarget.description,
                )}
              </Typography.Text>
            </span>
          ) : (
            "ประวัติเข้า-ออก"
          )
        }
        open={!!generalStockHistoryTarget}
        onCancel={() => setGeneralStockHistoryTarget(null)}
        footer={null}
        destroyOnClose
        width={720}
      >
        <Table
          size="small"
          loading={generalTxLoading}
          dataSource={generalTxRows}
          columns={generalTxColumns}
          rowKey="id"
          pagination={{
            current: generalStockHistoryPage,
            pageSize: historyLimit,
            total: generalTxTotal,
            showSizeChanger: false,
            onChange: (p) => setGeneralStockHistoryPage(p),
            showTotal: (t) => `ทั้งหมด ${t} รายการ`,
          }}
          scroll={{ y: 360 }}
          locale={{ emptyText: "ยังไม่มีประวัติ" }}
        />
      </Modal>

      {loadingView ? (
        <Skeleton active paragraph={{ rows: 6 }} />
      ) : !hasSourceData ? (
        <Empty>
          <Typography.Text type="secondary">ยังไม่มี{MASTER_STOCK_LABEL}</Typography.Text>
        </Empty>
      ) : searchTrimmed && !hasVisible ? (
        <Empty>
          <Typography.Text type="secondary">ไม่พบรายการที่ตรงกับคำค้นหา</Typography.Text>
        </Empty>
      ) : !hasVisible ? (
        <Empty>
          <Typography.Text type="secondary">ไม่มีรายการตามตัวกรอง</Typography.Text>
        </Empty>
      ) : (
        <MasterStockTable
          items={displayItems}
          onAdjustGeneral={openAdjustGeneralStock}
          onHistoryGeneral={openGeneralStockHistory}
          onSafeGeneral={setGeneralStockSafeTarget}
          onEditGeneral={setGeneralStockEdit}
          onDeleteGeneral={(item) =>
            deleteGeneralStockMutate(item.id, {
              onSuccess: () => message.success("ลบแล้ว"),
              onError: (err: any) => {
                if (!handleLinkedStockDependentsError(err)) message.error(apiErrorMessage(err, "ลบไม่สำเร็จ"));
              },
            })
          }
          deletingGeneral={deletingGeneralStock}
          onViewProduct={handleViewProduct}
          onEditSku={(skuId, productId) => setSkuEditTarget({ skuId, productId })}
          loading={inventoryFetching}
          sortBy={sortBy}
          sortDir={sortDir}
          onSortChange={(field, dir) => {
            setSortBy(field);
            setSortDir(dir);
          }}
        />
      )}

      {/* แก้ไข SKU ขั้นสูง — เปิด modal กำหนดสินค้า–ราคา/คลัง inline (เหมือนหน้าแก้ไขคลัง) */}
      {skuEditTarget && skuEditTarget.productId && (
        <InventorySkuEditModal
          productId={skuEditTarget.productId}
          skuId={skuEditTarget.skuId}
          open
          onClose={() => setSkuEditTarget(null)}
        />
      )}
    </Wrap>
  );
};

export default MasterStockPanel;

const generalInventoryCount = (products: ProductInventoryItem[]): number =>
  products.filter(isInventoryGeneralItem).length;
const skuInventoryCount = (products: ProductInventoryItem[]): number =>
  products.filter(isInventorySkuItem).length;

const Wrap = styled.div`
  padding: 4px 0;
`;

const Toolbar = styled.div`
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
  gap: 8px;
  padding: 0 0 12px;
  overflow-x: auto;
`;

const CategorySlot = styled.div`
  flex: 0 0 auto;

  /* ปุ่มหมวดที่ถูกเลือก — ทำสีให้เด่น (ฟ้า primary) */
  .ant-segmented-item-selected,
  .ant-segmented-thumb {
    background-color: #1677ff;
  }
  .ant-segmented-item-selected,
  .ant-segmented-item-selected:hover,
  .ant-segmented-item-selected .ant-segmented-item-label {
    color: #ffffff;
  }
`;

const Actions = styled.div`
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
  gap: 8px;
  margin-left: auto;
  flex-shrink: 0;
`;

const SearchBar = styled.div`
  width: 280px;
  max-width: 100%;
  padding: 0 0 12px;
  & > div {
    margin-bottom: 0;
    width: 100%;
  }
`;

const Empty = styled.div`
  padding: 48px;
  text-align: center;
  background: #fafafa;
  border-radius: 8px;
`;
